<template>
  <v-container v-if="step === 1" no-gutters>
    <v-row no-gutters class="pb-6">
      <v-col>
        <img ref="logo" style="max-height:220px" :src="url" />
      </v-col>
    </v-row>
    <v-row no-gutters class="justify-center pb-6">
      <v-col cols="auto">
        <v-subheader class="text-h6 pa-0 font--text font-weight-regular" style="height: auto;">
          {{ appName }}
        </v-subheader>
      </v-col>
    </v-row>
    <v-row no-gutters class="justify-center">
      <v-col cols="8" lg="7">
        <v-form>
          <authentication-input
            translation-path="login.emailOrUsername"
            autofocus
            hide-details
            v-model="credentials.username_or_email"
            @keyup.enter="loginHandler"
            class="pb-11"
            name="username"
          ></authentication-input>
          <authentication-input
            name="password"
            type="password"
            translation-path="login.password"
            v-model="credentials.password"
            hide-details
            @keyup.enter="loginHandler"
          ></authentication-input>
          <v-row no-gutters class="justify-start">
            <v-col cols="auto">
              <link-underlined
                translation-path="login.forgotPassword"
                @click="recoverPasswordHandler"
              ></link-underlined>
            </v-col>
          </v-row>
          <v-row no-gutters class="justify-end pt-6">
            <v-col cols="auto">
              <base-button
                :loading="isLoading"
                @click="loginHandler"
                translation-path="login.login"
                style="margin-right: 0px !important;"
              ></base-button>
            </v-col>
          </v-row>
        </v-form>
      </v-col>
    </v-row>
  </v-container>
  <v-container v-else no-gutters class="d-flex flex-column" style="gap: 2.5rem">
    <v-row no-gutters>
      <v-col cols="12" class="text-h4 text-center">
        {{ $i18n.t('login.2FATitle') }}
      </v-col>
    </v-row>
    <v-row no-gutters>
      <v-col cols="12" class="text-subtitle-1 text-center">
        {{ $i18n.t('login.2FASubtitle') }}
      </v-col>
    </v-row>
    <v-row no-gutters>
      <v-col cols="12" class="d-flex justify-center">
        <v-sheet min-width="300" max-width="400" @keyup.enter="loginHandler">
          <authentication-input
            name="2faCode"
            translation-path="login.2FAActivationCode"
            v-model="twoFACode"
            hide-details
            data-type-name="otp"
          ></authentication-input>
        </v-sheet>
      </v-col>
    </v-row>
    <v-row no-gutters class="align-center justify-center" style="gap: 1rem 3rem">
      <v-col cols="auto" class="d-flex align-center" style="gap: 0.5rem">
        <span class="text-caption">{{ $i18n.t('login.2FAEmailNotSent') }}</span>
        <span>
          <link-underlined translation-path="login.2FASendMailAgain" @click="sendCodeAgain"></link-underlined>
        </span>
      </v-col>
      <v-col cols="auto">
        <base-button
          :loading="isLoading"
          @click="mainLoginHandler"
          translation-path="login.login"
          style="margin-right: 0px !important;"
        ></base-button>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { call, get } from 'vuex-pathify';

import logo from '@/mixins/logo';

import AuthenticationInput from '@/components/AuthenticationInput';
import LinkUnderlined from '@/components/LinkUnderlined';

export default {
  name: 'Login',
  mixins: [logo],
  components: {
    AuthenticationInput,
    LinkUnderlined,
  },
  data: () => ({
    credentials: {
      password: undefined,
      username_or_email: undefined,
    },
    step: 1,
    unsuccessfulLogins: 0,
    twoFACode: null,
    url: `${process.env.VUE_APP_API_URL}/settings/logo`,
    isLoading: false,
  }),
  computed: {
    appName: get('admin/settingsValues@app_name'),
    smtpEnabled: get('admin/additionalModules@SMTP.enabled'),
    smtpConfigured: get('admin/additionalModules@SMTP.configured'),
    isSmtpWorking() {
      return this.smtpEnabled && this.smtpConfigured;
    },
  },
  methods: {
    createGtagEvent: call('gtag/createEvent'),
    login: call('authentication/login'),
    recoverPasswordHandler() {
      if (this.isSmtpWorking) {
        this.$router.push({ name: 'email' });
      } else {
        this.$store.set('snackbar/PUSH_MESSAGE!', {
          message: this.$i18n.t(`login.noSmtp`),
          color: '#333333',
          buttonType: 'text',
        });
      }
    },
    async sendCodeAgain() {
      await this.loginHandler();
      this.$store.set('snackbar/PUSH_MESSAGE!', {
        message: this.$i18n.t('admin.2FASendAgain'),
      });
    },
    async mainLoginHandler() {
      this.isLoading = true;
      try {
        if (this.step === 2) {
          await this.login({
            ...this.credentials,
            verification_code: this.twoFACode,
          });
        }
        this.createGtagEvent({
          action: 'login',
          params: {
            user: this.credentials.username_or_email,
          },
          extra_params: {
            method: 'private',
          },
        });
        this.$router.push(this.$route.query.redirect || '/map');
      } catch (error) {
        if (this.step === 2 && error.rawMessage.includes('MaxFailedVerificationCodeExceeded')) {
          this.step = 1;
          this.twoFACode = null;
          this.credentials = {
            password: undefined,
            username_or_email: undefined,
          };
        }
      } finally {
        this.isLoading = false;
      }
    },
    async loginHandler() {
      try {
        this.isLoading = true;
        const loginStatus = await this.login(this.credentials);
        loginStatus === 201 ? (this.step = 2) : this.mainLoginHandler();
      } catch {
        //
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped></style>
